import { Drawer, DrawerOverlay } from '@chakra-ui/react';
import { executeNextLoop } from '@ifixit/helpers';
import type { LocaleCode } from '@ifixit/i18n/types';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { Suspense, lazy, type ElementType } from 'react';
import { useIsMountedState } from '../../hooks';
import type { CartImageComponent } from '../types';
import { CartDrawerTrigger } from './CartDrawerTrigger';
import { useCartDrawer } from './hooks/useCartDrawer';

const LazyCartDrawerContent = lazy(() => import('./CartDrawerContent'));

interface CartDrawerProps {
   ImageComponent: CartImageComponent;
   linkComponent?: ElementType;
   supportLinkComponent: ElementType;
   localeCode: LocaleCode;
   viewCartUrl: string;
   storeCode: string;
   showMerchBanner?: boolean;
   recordMerchExperiment: () => boolean;
   /**
    * Link component used by the Pro promo banner when it replaces the merch
    * slot. Separate from `linkComponent` so callers can opt into the upsell
    * without changing the cart's own link rendering.
    */
   promoLinkComponent?: ElementType;
}

export function CartDrawer({
   ImageComponent,
   linkComponent,
   localeCode,
   supportLinkComponent,
   viewCartUrl,
   storeCode,
   recordMerchExperiment,
   promoLinkComponent,
}: CartDrawerProps) {
   const { isOpen, onOpen, onClose } = useCartDrawer();
   const isMounted = useIsMountedState();
   const cart = useCart();
   const cartData = cart.data ?? null;

   return (
      <>
         <CartDrawerTrigger
            onClick={() => executeNextLoop(onOpen)}
            hasItemsInCart={cartData?.hasItemsInCart}
         />
         {isMounted && (
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
               <DrawerOverlay />
               {isOpen && (
                  <Suspense>
                     <LazyCartDrawerContent
                        ImageComponent={ImageComponent}
                        linkComponent={linkComponent}
                        supportLinkComponent={supportLinkComponent}
                        localeCode={localeCode}
                        viewCartUrl={viewCartUrl}
                        storeCode={storeCode}
                        recordMerchExperiment={recordMerchExperiment}
                        promoLinkComponent={promoLinkComponent}
                     />
                  </Suspense>
               )}
            </Drawer>
         )}
      </>
   );
}
